import api from './apiInstance';

export interface Room {
  id: number;
  roomId?: string; // Added
  name: string;
  description: string;
  address: string;
  city?: string; // Added city field
  isTest?: boolean; // Added isTest flag (default false)
  scheduleActive?: number; // 0=inactive,1=manual,2=automatic Friday sermon
  scheduleDayOfWeek?: number; // 0=Sun ... 5=Fri (API spec 0-5)
  scheduleStartTime?: string | null; // HH:MM or HH:MM:SS
  scheduleEndTime?: string | null; // HH:MM or HH:MM:SS
  lng: number | null;
  lat: number | null;
  geoRestricted?: boolean;
  geoCenterLat?: number | null;
  geoCenterLng?: number | null;
  geoRadiusMeters?: number | null;
  geoLabel?: string | null;
  fromLanguage: string;
  toLanguages: string | string[]; // Can be JSON string or array
  toLanguagesArray?: string[]; // Parsed array
  roomType?: string; // Added
  enableTranscription?: boolean; // Added
  showTranslationText?: boolean; // Added show translation text field
  phrases?: string | null;
  blockWords?: string | null;
  wordsReplacements?: string | null;
  sttModel?: string | null;
  translationModel?: string | null;
  ttsModel?: string | null;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  company?: {
    name: string;
    logo?: string;
  };
  speakerPassword?: string;
  listenersPassword?: string;
  speakerUrl?: string; // URL for speaker to join
  listenerUrl?: string; // URL for listeners to join
  listenerRedirectUrl?: string | null;
  usageTime?: string; // For UI display
}

export interface RoomListResponse {
  rooms: Room[];
  total: number;
  pages: number;
  currentPage: number;
}

// Next schedule response (automatic Friday sermon)
export interface NextScheduleData {
  nextScheduleUtc?: string | null;
  reference?: any | null;
}

export interface RoomQueryParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

// Get all rooms with pagination
export const getRooms = async (params?: RoomQueryParams): Promise<RoomListResponse> => {
  try {
    const response = await api.get('/rooms', { params });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch rooms:', error);
    throw error;
  }
};

// Get a single room by ID
export const getRoom = async (id: number): Promise<Room> => {
  try {
    const response = await api.get(`/rooms/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch room with ID ${id}:`, error);
    throw error;
  }
};

// Create a new room
export const createRoom = async (roomData: Partial<Room>): Promise<Room> => {
  try {
    const response = await api.post('/rooms', roomData);
    return response.data.data;
  } catch (error) {
    console.error('Failed to create room:', error);
    throw error;
  }
};

// Update an existing room
export const updateRoom = async (id: number, roomData: Partial<Room>): Promise<Room> => {
  try {
    const response = await api.put(`/rooms/${id}`, roomData);
    return response.data.data;
  } catch (error) {
    console.error(`Failed to update room with ID ${id}:`, error);
    throw error;
  }
};

// Delete a room
export const deleteRoom = async (id: number): Promise<void> => {
  try {
    await api.delete(`/rooms/${id}`);
  } catch (error) {
    console.error(`Failed to delete room with ID ${id}:`, error);
    throw error;
  }
};

// Helper functions to parse and stringify toLanguages
export const parseToLanguages = (room: Room): Room => {
  if (typeof room.toLanguages === 'string') {
    try {
      const toLanguagesArray = JSON.parse(room.toLanguages);
      return { ...room, toLanguagesArray };
    } catch (error) {
      console.error('Failed to parse toLanguages:', error);
    }
  }
  return { ...room, toLanguagesArray: [] };
};

export const formatRoomUsageTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours} hr ${minutes.toString().padStart(2, '0')} min ${seconds.toString().padStart(2, '0')}sec`;
};

// Fetch next automatic Friday sermon schedule (room id 0 per backend convention)
export const getNextSchedule = async (city: string, country?: string): Promise<NextScheduleData> => {
  try {
    const params: Record<string, string> = {};
    if (city) params.city = city;
    if (country) params.country = country;
    const response = await api.get('/rooms/0/next-schedule', { params });
    return response.data?.data || {};
  } catch (error) {
    console.error('Failed to fetch next schedule:', error);
    throw error;
  }
};

// Remote-start recording via admin panel. Electron speaker polls next-schedule every 5s
// and will auto-start recording once the server returns an active window.
export interface RemoteRecordingStatus {
  roomId: string | null;
  id: number;
  name?: string;
  active: boolean;
  startAt?: string;
  endAt?: string;
  // Liveness of the Electron desktop speaker for this room.
  clientOnline?: boolean;
  lastPollAt?: string | null;
  lastSeenMsAgo?: number | null;
  // Electron's own reported recording state (null when it has never polled).
  clientRecording?: boolean | null;
  onlineThresholdMs?: number;
  // Audio pipeline health: filled in once the speaker service has produced at least one
  // transcription for the current recording session.
  lastTranscriptionAt?: string | null;
  lastTranscriptionMsAgo?: number | null;
  audioReceiving?: boolean;
  audioReceivingThresholdMs?: number;
}

export const startRemoteRecording = async (
  id: number | string,
  durationMinutes?: number
): Promise<RemoteRecordingStatus> => {
  const body: Record<string, number> = {};
  if (durationMinutes) body.durationMinutes = durationMinutes;
  const response = await api.post(`/rooms/${id}/remote-start`, body);
  return response.data.data;
};

export const stopRemoteRecording = async (
  id: number | string
): Promise<RemoteRecordingStatus> => {
  const response = await api.post(`/rooms/${id}/remote-stop`, {});
  return response.data.data;
};

export const getRemoteRecordingStatus = async (
  id: number | string
): Promise<RemoteRecordingStatus> => {
  const response = await api.get(`/rooms/${id}/remote-status`);
  return response.data.data;
};

export interface BulkRemoteRecordingResult {
  id: number | string;
  success: boolean;
  data?: RemoteRecordingStatus;
  error?: string;
}

export interface BulkRemoteRecordingResponse {
  results: BulkRemoteRecordingResult[];
  total: number;
  successes: number;
}

export const startRemoteRecordingBulk = async (
  ids: Array<number | string>,
  durationMinutes?: number
): Promise<BulkRemoteRecordingResponse> => {
  const body: Record<string, unknown> = { ids };
  if (durationMinutes) body.durationMinutes = durationMinutes;
  const response = await api.post('/rooms/remote-start/bulk', body);
  return response.data.data;
};

export const stopRemoteRecordingBulk = async (
  ids: Array<number | string>
): Promise<BulkRemoteRecordingResponse> => {
  const response = await api.post('/rooms/remote-stop/bulk', { ids });
  return response.data.data;
};

export interface BulkRemoteStatusResult {
  identifier: number | string;
  success: boolean;
  data?: RemoteRecordingStatus;
  error?: string;
}

export interface BulkRemoteStatusResponse {
  results: BulkRemoteStatusResult[];
  total: number;
  successes: number;
}

/**
 * Batch remote-status lookup. Accepts an array of room numeric ids or string roomIds
 * (or any mix) and returns one entry per input. Collapses what used to be N HTTP calls
 * into 1, which matters a lot once the admin panel is watching >20 rooms.
 */
export const getRemoteRecordingStatusBulk = async (
  ids: Array<number | string>
): Promise<BulkRemoteStatusResponse> => {
  const response = await api.post('/rooms/remote-status/bulk', { ids });
  return response.data.data;
};
